import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import apiClient from '../api/client'

interface TopicDoc {
  doc_id: number
  doi: string | null
  openalex_id: string | null
  preview: string
  page_content?: string
  cluster_id: number
  assigned_topic_prob: number | null
  topic_prob_distribution?: number[] | null
}

interface Topic {
  topic_id: number
  topic_name: string
  n_docs: number
  compactness: number | null
  redundancy: number | null
  effective_information_score: number | null
  embedding_clusters_present?: number[]
  documents?: TopicDoc[]
  subclusters?: { subcluster_id: number; n_docs: number; doc_ids: number[]; keywords: string[] }[]
}

interface IterationResponse {
  iteration_index: number
  clustering_analysis: { topics?: Topic[] } | null
}

function TopicDetailPage(): JSX.Element {
  const { id, iteration, topicId } = useParams<{ id: string; iteration: string; topicId: string }>()
  const [topic, setTopic] = useState<Topic | null>(null)
  const [iterationData, setIterationData] = useState<IterationResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedDoc, setSelectedDoc] = useState<TopicDoc | null>(null)

  useEffect(() => {
    if (!id || iteration === undefined || topicId === undefined) return
    const fetch = async () => {
      try {
        const res = await apiClient.get<IterationResponse[]>(`/research/runs/${id}/iterations`)
        const iters = res.data as unknown as IterationResponse[]
        const iterIdx = Number(iteration)
        const tId = Number(topicId)
        const it = iters.find((it) => it.iteration_index === iterIdx)
        if (!it) throw new Error(`Iteration ${iterIdx} not found`)
        setIterationData(it)
        const t = it.clustering_analysis?.topics?.find((tp) => tp.topic_id === tId)
        if (!t) throw new Error(`Topic ${tId} not found in iteration ${iterIdx}`)
        setTopic(t)
      } catch (err: unknown) {
        const maybeAxios = err as { response?: { data?: { detail?: string } }; message?: string }
        setError(maybeAxios?.response?.data?.detail ?? maybeAxios?.message ?? 'Failed to load topic')
      } finally {
        setLoading(false)
      }
    }
    fetch()
  }, [id, iteration, topicId])

  if (loading) return <div className="page-header"><h1 className="page-title">Loading topic...</h1></div>
  if (error) return <div><p style={{ color: '#c00' }}>{error}</p><Link to={`/runs/${id}`}>Back to Run</Link></div>
  if (!topic || !iterationData) return <div>Topic not found</div>

  const docs = topic.documents ?? []
  const subclusters = topic.subclusters ?? []

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {topic.topic_name} <span style={{ fontSize: 14, fontWeight: 400, color: '#64748b' }}>#{topic.topic_id} · Iteration {iteration}</span>
          </h1>
          <p style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
            {topic.n_docs} docs · compactness {topic.compactness?.toFixed(2) ?? '-'} · redundancy {topic.redundancy?.toFixed(2) ?? '-'} · effective {topic.effective_information_score?.toFixed(2) ?? '-'}
            {topic.embedding_clusters_present && ` · clusters ${topic.embedding_clusters_present.join(', ')}`}
          </p>
        </div>
        <Link to={`/runs/${id}`} className="page-action-secondary">Back to Run</Link>
      </div>

      {subclusters.length > 0 && (
        <div className="detail-card" style={{ marginBottom: 16 }}>
          <h2 className="detail-card-title">Subclusters</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {subclusters.map((sc) => (
              <div key={sc.subcluster_id} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>Subcluster {sc.subcluster_id} — {sc.n_docs} docs</div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>Keywords: {sc.keywords.join(', ')}</div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>Doc IDs: {sc.doc_ids.join(', ')}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="detail-card detail-card-wide">
        <h2 className="detail-card-title">Documents in topic — {docs.length}</h2>
        {docs.length === 0 ? (
          <p style={{ fontSize: 14, color: '#666' }}>No documents in this topic.</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 70 }}>Doc ID</th>
                <th>Title / Preview</th>
                <th style={{ width: 90, textAlign: 'right' }}>Prob</th>
                <th style={{ width: 80, textAlign: 'right' }}>Cluster</th>
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.doc_id} style={{ cursor: 'pointer' }} onClick={() => setSelectedDoc(d)}>
                  <td>
                    <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{d.doc_id}</span>
                    {d.doi && <div style={{ fontSize: 11, color: '#2563eb', wordBreak: 'break-all' }}>{d.doi}</div>}
                    {d.openalex_id && !d.doi && <div style={{ fontSize: 11, color: '#2563eb', wordBreak: 'break-all' }}>{d.openalex_id}</div>}
                  </td>
                  <td style={{ maxWidth: 420 }}>
                    <div
                      style={{ fontWeight: 500, color: 'var(--primary)', cursor: 'pointer' }}
                      onClick={(e) => {
                        e.stopPropagation()
                        setSelectedDoc(d)
                      }}
                    >
                      {d.preview.slice(0, 120)}
                    </div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 4, whiteSpace: 'normal', wordBreak: 'break-word' }}>{d.preview.slice(0, 200)}</div>
                  </td>
                  <td style={{ textAlign: 'right', fontSize: 13 }}>{d.assigned_topic_prob != null ? d.assigned_topic_prob.toFixed(2) : '-'}</td>
                  <td style={{ textAlign: 'right' }}>{d.cluster_id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedDoc && (
        <div
          onClick={() => setSelectedDoc(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: 12, maxWidth: 720, width: '100%', maxHeight: '80vh', overflow: 'auto', padding: 24, boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h2 style={{ fontSize: 16, fontWeight: 700 }}>Document {selectedDoc.doc_id}</h2>
              <button onClick={() => setSelectedDoc(null)} style={{ border: 'none', background: '#f1f5f9', borderRadius: 8, padding: '6px 12px', cursor: 'pointer' }}>Close</button>
            </div>
            <div style={{ fontSize: 13, color: '#475569', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {selectedDoc.doi && (
                <div>
                  <strong>DOI:</strong>{' '}
                  <a href={selectedDoc.doi.startsWith('http') ? selectedDoc.doi : `https://doi.org/${selectedDoc.doi}`} target="_blank" rel="noreferrer" style={{ color: 'var(--primary)' }}>
                    {selectedDoc.doi}
                  </a>
                </div>
              )}
              {selectedDoc.openalex_id && (
                <div>
                  <strong>OpenAlex:</strong>{' '}
                  <a href={selectedDoc.openalex_id} target="_blank" rel="noreferrer" style={{ color: 'var(--primary)' }}>
                    {selectedDoc.openalex_id}
                  </a>
                </div>
              )}
              <div><strong>Cluster:</strong> {selectedDoc.cluster_id}</div>
              <div><strong>Topic prob:</strong> {selectedDoc.assigned_topic_prob?.toFixed(3) ?? '-'}</div>
              {selectedDoc.topic_prob_distribution && <div style={{ fontSize: 11, color: '#94a3b8' }}>Distribution: [{selectedDoc.topic_prob_distribution.slice(0, 6).map((v) => (typeof v === 'number' ? v.toFixed(2) : String(v))).join(', ')}{selectedDoc.topic_prob_distribution.length > 6 ? ', …' : ''}]</div>}
              <div style={{ marginTop: 8 }}>
                <strong>Document:</strong>
                <p style={{ whiteSpace: 'pre-wrap', marginTop: 4, background: '#f8fafc', padding: 12, borderRadius: 8, maxHeight: 400, overflow: 'auto' }}>{selectedDoc.page_content ?? selectedDoc.preview}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TopicDetailPage
